import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fetchEarnings } from "../lib/api";
import { artistName } from "../lib/artists";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui/table";

const money = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n || 0);

const thisMonth = () => new Date().toISOString().slice(0, 7);
const shiftMonth = (ym, d) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1 + d, 1)).toISOString().slice(0, 7);
};
const monthLabel = (ym) =>
  new Date(`${ym}-01T00:00:00Z`).toLocaleDateString("en-AU", { month: "long", year: "numeric", timeZone: "UTC" });

export default function AdminEarnings() {
  const [allTime, setAllTime] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ym, setYm] = useState(thisMonth);
  const [mode, setMode] = useState("month"); // "month" | "all"

  useEffect(() => {
    (async () => {
      try {
        setAllTime(await fetchEarnings());
      } catch {
        toast.error("Failed to load earnings");
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        setData(await fetchEarnings(mode === "month" ? ym : undefined));
      } catch {
        toast.error("Failed to load earnings");
      } finally {
        setLoading(false);
      }
    })();
  }, [ym, mode]);

  const periodLabel = mode === "month" ? monthLabel(ym) : "All time";

  return (
    <div data-testid="admin-earnings" className="space-y-8">
      {/* All-time totals — always visible */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div data-testid="earnings-total-commission" className="bg-[#0A0A0A] text-white p-6 order-first md:order-none">
          <div className="overline mb-2 text-white/60">Studio Earnings (30%) · All time</div>
          <div className="font-display text-4xl md:text-5xl">{money(allTime?.totals.commission)}</div>
        </div>
        <div data-testid="earnings-total-revenue" className="bg-white border border-[#E5E5E5] p-6">
          <div className="overline mb-2">Total Charged · All time</div>
          <div className="font-display text-3xl md:text-4xl">{money(allTime?.totals.charged)}</div>
        </div>
        <div data-testid="earnings-total-payouts" className="bg-white border border-[#E5E5E5] p-6">
          <div className="overline mb-2">Artist Share (70%) · All time</div>
          <div className="font-display text-3xl md:text-4xl">{money(allTime?.totals.artist_net)}</div>
        </div>
      </div>

      {/* Period picker */}
      <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-[#E5E5E5]">
        <div>
          <div className="overline mb-1">By artist</div>
          <div className="font-display text-2xl" style={{ letterSpacing: "0.04em", fontWeight: 200 }} data-testid="earnings-period-label">{periodLabel}</div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`inline-flex items-center border bg-white ${mode === "month" ? "border-[#0A0A0A]" : "border-[#E5E5E5]"}`} data-testid="earnings-month-picker">
            <button onClick={() => { setMode("month"); setYm(shiftMonth(ym, -1)); }} data-testid="earnings-month-prev" className="p-2.5 hover:bg-[#FAFAFA]"><ChevronLeft size={14} /></button>
            <button onClick={() => setMode("month")} data-testid="earnings-month-label" className="text-[10px] tracking-[0.25em] uppercase px-3 min-w-[130px] text-center">{monthLabel(ym)}</button>
            <button onClick={() => { setMode("month"); setYm(shiftMonth(ym, 1)); }} data-testid="earnings-month-next" className="p-2.5 hover:bg-[#FAFAFA]"><ChevronRight size={14} /></button>
          </div>
          <button
            onClick={() => setMode("all")}
            data-testid="earnings-all-time"
            className={`text-[10px] tracking-[0.25em] uppercase px-4 py-2.5 border transition-colors ${mode === "all" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#E5E5E5] hover:border-[#0A0A0A]"}`}
          >
            All time
          </button>
          {mode === "month" && ym !== thisMonth() && (
            <button onClick={() => setYm(thisMonth())} data-testid="earnings-this-month" className="text-[10px] tracking-[0.25em] uppercase px-3 py-2.5 text-[#999] hover:text-[#0A0A0A]">Today</button>
          )}
        </div>
      </div>

      {/* Period summary */}
      {data && (
        <div className="grid grid-cols-3 gap-4" data-testid="earnings-period-cards">
          <div className="bg-white border border-[#0A0A0A] p-4">
            <div className="overline mb-1">Studio (30%)</div>
            <div className="font-display text-2xl md:text-3xl" data-testid="earnings-period-commission">{money(data.totals.commission)}</div>
          </div>
          <div className="bg-white border border-[#E5E5E5] p-4">
            <div className="overline mb-1">Charged</div>
            <div className="font-display text-2xl md:text-3xl" data-testid="earnings-period-charged">{money(data.totals.charged)}</div>
          </div>
          <div className="bg-white border border-[#E5E5E5] p-4">
            <div className="overline mb-1">Appointments</div>
            <div className="font-display text-2xl md:text-3xl" data-testid="earnings-period-appointments">{data.totals.appointments}</div>
          </div>
        </div>
      )}

      {/* Per-artist table */}
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        {loading || !data ? (
          <div className="py-14 text-center text-[#666] text-sm tracking-widest uppercase">Loading…</div>
        ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="overline">Artist</TableHead>
              <TableHead className="overline text-right">Appointments</TableHead>
              <TableHead className="overline text-right">Charged</TableHead>
              <TableHead className="overline text-right">Artist Share (70%)</TableHead>
              <TableHead className="overline text-right font-medium text-[#0A0A0A]">Studio Earnings (30%)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.artists.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-14 text-center text-[#999] text-sm tracking-widest uppercase" data-testid="earnings-empty">
                  No completed appointments with payments in {periodLabel.toLowerCase()}.
                </TableCell>
              </TableRow>
            ) : (
              <>
                {data.artists.map((a) => (
                  <TableRow key={a.artist} data-testid={`earnings-row-${a.artist}`}>
                    <TableCell className="text-sm uppercase tracking-[0.15em]">{artistName(a.artist)}</TableCell>
                    <TableCell className="text-right text-sm">{a.appointments}</TableCell>
                    <TableCell className="text-right text-sm">{money(a.charged)}</TableCell>
                    <TableCell className="text-right text-sm text-[#666]">{money(a.artist_net)}</TableCell>
                    <TableCell className="text-right text-sm font-semibold">{money(a.commission)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 border-[#0A0A0A] bg-[#FAFAFA]" data-testid="earnings-totals-row">
                  <TableCell className="text-sm uppercase tracking-[0.2em] font-medium">Total · {periodLabel}</TableCell>
                  <TableCell className="text-right text-sm font-medium">{data.totals.appointments}</TableCell>
                  <TableCell className="text-right text-sm font-medium">{money(data.totals.charged)}</TableCell>
                  <TableCell className="text-right text-sm font-medium text-[#666]">{money(data.totals.artist_net)}</TableCell>
                  <TableCell className="text-right text-sm font-semibold">{money(data.totals.commission)}</TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
        )}
      </div>
    </div>
  );
}
